import { useCallback, useEffect, useRef, useState } from "react";

const agora = () =>
  typeof performance !== "undefined" ? performance.now() : Date.now();

/**
 * Impede que o arrasto vire seleção de texto.
 *
 * Duas coisas que pareciam bastar e não bastam, as duas medidas:
 *
 * - **`user-select: none` na pista.** Com ele aplicado, arrastar ainda deixava
 *   456 caracteres selecionados. A propriedade impede que o conteúdo DAQUELE
 *   elemento seja selecionado; não impede o navegador de iniciar uma seleção a
 *   partir do gesto e estendê-la pelo resto do documento — que aqui é o título e
 *   o enunciado da atividade, pintados de azul atrás da trilha. Acontece de
 *   verdade quando o cursor sai da pista pela borda, o que é comum: com
 *   `setPointerCapture` os eventos continuam vindo, mas a seleção nativa segue o
 *   mouse pela página.
 * - **Limpar com `removeAllRanges` durante o arrasto.** O navegador recria a
 *   seleção no movimento seguinte; é enxugar gelo.
 *
 * O que resolve é bloquear na origem: `selectstart` é o evento que a inicia, e
 * cancelá-lo enquanto durar o gesto impede que ela exista. No DOCUMENTO e não na
 * pista, porque é justamente quando o cursor sai dela que o problema aparece.
 *
 * `preventDefault` no `pointerdown` também resolveria, e foi descartado: mata
 * junto o foco do que estiver sob o cursor, e sob o cursor costuma estar um nó.
 */
function bloquearSelecao() {
  if (typeof document === "undefined") return () => {};
  const cancelar = evento => evento.preventDefault();
  document.addEventListener("selectstart", cancelar);
  return () => document.removeEventListener("selectstart", cancelar);
}

// ---------------------------------------------------------------------------
// O MOVIMENTO
//
// A rolagem é animada aqui, e não com `behavior: "smooth"` nem com
// `scroll-behavior` no CSS, por dois motivos:
//
// 1. O `smooth` nativo tem curva e duração fechadas, que variam por navegador.
//    A trilha já tem uma linguagem de movimento própria — a caminhada do
//    explorador, a cortina de neblina —, e a câmera destoava dela.
// 2. `scroll-behavior: smooth` no CSS afetaria TAMBÉM a escrita direta de
//    `scrollLeft`, que é como o arrasto acompanha o cursor. A trilha passaria a
//    perseguir o mouse com atraso, e arrastar viraria elástico.

// Desacelera até parar. É a curva de algo que foi empurrado e assenta, que é
// exatamente a metáfora — e o contrário de `ease-in-out`, que faria a trilha
// "arrancar" a cada clique de seta.
function suavizar(t) {
  return 1 - (1 - t) ** 3;
}

function prefereMenosMovimento() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (erro) {
    return false;
  }
}

function limitar(pista, valor) {
  return Math.max(0, Math.min(valor, pista.scrollWidth - pista.clientWidth));
}

/**
 * Leva a pista até um ponto, animando.
 *
 * Devolve uma função que cancela — e cancelar importa: se o aluno arrastar ou
 * clicar noutra seta no meio do caminho, a animação antiga continuaria disputando
 * o `scrollLeft` com a mão dele.
 *
 * Sem `requestAnimationFrame` (jsdom) ou com movimento reduzido, salta direto: o
 * destino é o mesmo, só não há percurso.
 */
function animarAte(pista, destino, duracao, aoTerminar) {
  const alvo = limitar(pista, destino);
  const partida = pista.scrollLeft;
  const delta = alvo - partida;

  if (
    !delta ||
    prefereMenosMovimento() ||
    typeof requestAnimationFrame === "undefined"
  ) {
    pista.scrollLeft = alvo;
    if (aoTerminar) aoTerminar();
    return () => {};
  }

  let quadro = null;
  const inicio =
    typeof performance !== "undefined" ? performance.now() : Date.now();

  const passo = instante => {
    const t = Math.min(1, (instante - inicio) / duracao);
    pista.scrollLeft = partida + delta * suavizar(t);
    if (t < 1) {
      quadro = requestAnimationFrame(passo);
      return;
    }
    quadro = null;
    if (aoTerminar) aoTerminar();
  };
  quadro = requestAnimationFrame(passo);

  return () => {
    if (quadro !== null) cancelAnimationFrame(quadro);
    quadro = null;
    if (aoTerminar) aoTerminar();
  };
}

// Duração proporcional à distância, com piso e teto. Distância fixa em tempo
// fixo faria um clique de seta numa trilha espalhada parecer arranco, e um
// deslocamento curto parecer arrasto lento.
const DURACAO_MINIMA = 260;
const DURACAO_MAXIMA = 700;
const VELOCIDADE_DA_CAMERA = 1.4; // px por ms

function duracaoPara(distancia) {
  return Math.min(
    DURACAO_MAXIMA,
    Math.max(DURACAO_MINIMA, Math.abs(distancia) / VELOCIDADE_DA_CAMERA)
  );
}

// ---------------------------------------------------------------------------
// INÉRCIA
//
// Soltar o arrasto parava a trilha seca, e é isso que denuncia que ela não é um
// objeto — coisa que se empurra continua andando e desacelera. Sem inércia o
// gesto é funcional e sem peso.
//
// Só para o arrasto de MOUSE. No celular quem faz isso é o scroll nativo, com
// uma curva que o sistema calibra por dispositivo.
const ATRITO = 0.94;
// Abaixo disto o movimento não é mais legível como deslocamento, só como tremor.
const VELOCIDADE_MINIMA = 0.05;
// Teto do impulso. Sem ele, um puxão forte atravessa a trilha inteira e o aluno
// perde de vista onde estava.
const VELOCIDADE_MAXIMA = 2.5;

function lancar(pista, velocidadeInicial, aoParar) {
  let v = Math.max(
    -VELOCIDADE_MAXIMA,
    Math.min(VELOCIDADE_MAXIMA, velocidadeInicial)
  );

  if (
    Math.abs(v) < VELOCIDADE_MINIMA ||
    prefereMenosMovimento() ||
    typeof requestAnimationFrame === "undefined"
  ) {
    if (aoParar) aoParar();
    return () => {};
  }

  let quadro = null;
  let anterior =
    typeof performance !== "undefined" ? performance.now() : Date.now();

  const passo = agora => {
    const dt = Math.min(32, agora - anterior) || 16;
    anterior = agora;
    v *= ATRITO ** (dt / 16);

    const antes = pista.scrollLeft;
    pista.scrollLeft = limitar(pista, antes + v * dt);
    // Bateu na borda: insistir só gastaria quadros.
    const parou = Math.abs(pista.scrollLeft - antes) < 0.5;

    if (Math.abs(v) < VELOCIDADE_MINIMA || parou) {
      quadro = null;
      if (aoParar) aoParar();
      return;
    }
    quadro = requestAnimationFrame(passo);
  };
  quadro = requestAnimationFrame(passo);

  return () => {
    if (quadro !== null) cancelAnimationFrame(quadro);
    quadro = null;
    if (aoParar) aoParar();
  };
}

/**
 * Como o aluno percorre a trilha sem depender da barra de rolagem.
 *
 * São três camadas, e a ordem é deliberada — a de baixo é a que mais gente
 * alcança:
 *
 * 1. **O scroll nativo continua existindo.** É ele que dá teclado (o Tab entre
 *    os nós rola a pista sozinho), leitor de tela e o gesto de arrastar o dedo
 *    no celular. Nada aqui o substitui; tudo aqui se soma a ele.
 * 2. **Setas nas bordas.** É a camada DESCOBRÍVEL, e a razão de ela vir antes do
 *    arrastar: o problema relatado não era o scroll ser ruim de usar, era não
 *    dar para perceber que havia mais trilha adiante. Arrastar resolve o "como";
 *    a seta resolve o "existe algo lá".
 * 3. **Arrastar o cenário**, como se puxa um mapa. É a camada mais gostosa e a
 *    menos descobrível — por isso é a última, e por isso nada depende só dela.
 *
 * A literatura de acessibilidade é unânime em que arrastar nunca pode ser o
 * único caminho, e é exatamente por isso que as três convivem.
 *
 * @param {object} pistaRef ref do elemento rolável
 * @param {number} passo    px que uma seta desloca (o vão entre dois nós)
 */
export function useNavegacaoDaPista(pistaRef, passo) {
  // Se não há para onde ir, a seta daquele lado não aparece — uma seta que não
  // faz nada é pior do que seta nenhuma para quem está aprendendo a ler.
  const [alcance, setAlcance] = useState({ voltar: false, avancar: false });
  const [arrastando, setArrastando] = useState(false);
  // Arrasto OU animação. O encaixe tem de ficar desligado nos dois: durante uma
  // animação programada ele PUXA a pista para o nó mais próximo a cada quadro, e
  // o resultado é a rolagem saltar direto para o destino — medido, uma seta que
  // deveria levar 260ms chegava em menos de 70. `arrastando` sozinho não cobre,
  // porque a seta anima sem ninguém arrastar nada.
  const [movendo, setMovendo] = useState(false);

  // Onde o arrasto começou, e se ele chegou a virar arrasto de verdade.
  const inicio = useRef(null);
  const arrastou = useRef(false);
  // Como desfazer o bloqueio de seleção quando o gesto acabar.
  const soltarSelecao = useRef(null);
  // Cancela a animação em curso — de seta ou de inércia. Guardar UMA só é
  // deliberado: as duas disputam o mesmo `scrollLeft`, e a que chega depois tem
  // de interromper a anterior em vez de somar.
  const pararMovimento = useRef(null);
  // Últimos pontos do arrasto, para estimar com que velocidade a mão soltou.
  const rastro = useRef([]);

  const interromper = useCallback(() => {
    if (pararMovimento.current) {
      pararMovimento.current();
      pararMovimento.current = null;
    }
    setMovendo(false);
  }, []);

  const medirAlcance = useCallback(() => {
    const pista = pistaRef.current;
    if (!pista) return;
    // 2px de tolerância: `scrollLeft` é fracionário em telas com zoom ou DPR
    // não inteiro, e sem folga a seta pisca no fim do percurso.
    const fim = pista.scrollWidth - pista.clientWidth;
    setAlcance(atual => {
      const voltar = pista.scrollLeft > 2;
      const avancar = pista.scrollLeft < fim - 2;
      if (atual.voltar === voltar && atual.avancar === avancar) return atual;
      return { voltar, avancar };
    });
  }, [pistaRef]);

  // Um arrasto interrompido por desmontagem (o aluno concluiu a fase e a trilha
  // trocou de leva) deixaria o bloqueio de seleção preso no documento, e a
  // página inteira ficaria sem poder selecionar texto.
  useEffect(
    () => () => {
      if (soltarSelecao.current) soltarSelecao.current();
      if (pararMovimento.current) pararMovimento.current();
    },
    []
  );

  useEffect(() => {
    const pista = pistaRef.current;
    if (!pista) return undefined;
    medirAlcance();
    pista.addEventListener("scroll", medirAlcance, { passive: true });
    return () => pista.removeEventListener("scroll", medirAlcance);
  }, [pistaRef, medirAlcance]);

  const deslizar = useCallback(
    direcao => {
      const pista = pistaRef.current;
      if (!pista) return;
      interromper();
      const quanto = (passo || pista.clientWidth * 0.8) * direcao;
      setMovendo(true);
      pararMovimento.current = animarAte(
        pista,
        pista.scrollLeft + quanto,
        duracaoPara(quanto),
        () => {
          setMovendo(false);
          pararMovimento.current = null;
        }
      );
    },
    [pistaRef, passo, interromper]
  );

  // ---------------------------------------------------------------- arrastar
  //
  // Pointer events, e não mouse+touch separados: é uma API só para as duas
  // entradas, e `setPointerCapture` faz o arrasto continuar mesmo quando o
  // ponteiro sai da pista — sem isso, puxar rápido "solta" a trilha no meio.
  //
  // Só para MOUSE. No celular o dedo já arrasta a pista pelo scroll nativo, com
  // inércia e resistência que nenhuma reimplementação em JS iguala; interceptar
  // ali só brigaria com o gesto do sistema.
  const aoApontar = useCallback(
    evento => {
      if (evento.pointerType !== "mouse" || evento.button !== 0) return;
      const pista = pistaRef.current;
      if (!pista || pista.scrollWidth <= pista.clientWidth) return;

      // Pegar a trilha no meio de um deslize a segura na hora, como se fosse um
      // objeto. Sem isto a animação continuaria por baixo da mão.
      interromper();
      setMovendo(true);
      inicio.current = { x: evento.clientX, scroll: pista.scrollLeft };
      arrastou.current = false;
      rastro.current = [{ x: evento.clientX, t: agora() }];
      // O bloqueio entra JÁ AQUI, e não quando o arrasto passa do limiar: o
      // `selectstart` dispara junto com o `pointerdown`, então instalá-lo depois
      // do primeiro movimento chega tarde — medido, a seleção já valia 456
      // caracteres. Sai no `pointerup` de qualquer jeito, e um clique simples
      // não seleciona nada, então antecipar não custa.
      soltarSelecao.current = bloquearSelecao();
    },
    [pistaRef]
  );

  const aoMover = useCallback(
    evento => {
      if (!inicio.current) return;
      const pista = pistaRef.current;
      if (!pista) return;

      const dx = evento.clientX - inicio.current.x;
      // LIMIAR. Sem ele, o tremor da mão entre apertar e soltar o botão contaria
      // como arrasto e engoliria o clique no nó — o aluno clicaria na fase e nada
      // aconteceria, de vez em quando.
      if (!arrastou.current && Math.abs(dx) < 4) return;

      if (!arrastou.current) {
        arrastou.current = true;
        setArrastando(true);
        if (pista.setPointerCapture) {
          pista.setPointerCapture(evento.pointerId);
        }
      }
      pista.scrollLeft = inicio.current.scroll - dx;

      // Só os últimos ~90ms entram na conta da velocidade: um arrasto que
      // demorou, parou e voltou a andar tem de ser lançado pelo FIM do gesto, e
      // não pela média dele.
      const t = agora();
      rastro.current.push({ x: evento.clientX, t });
      while (rastro.current.length > 2 && t - rastro.current[0].t > 90) {
        rastro.current.shift();
      }
    },
    [pistaRef]
  );

  const aoSoltar = useCallback(
    evento => {
      const pista = pistaRef.current;
      if (pista && pista.releasePointerCapture && evento.pointerId != null) {
        try {
          pista.releasePointerCapture(evento.pointerId);
        } catch (erro) {
          // O ponteiro já pode ter sido liberado (saiu da janela, por exemplo).
        }
      }
      const arrastouMesmo = arrastou.current;
      inicio.current = null;
      if (soltarSelecao.current) {
        soltarSelecao.current();
        soltarSelecao.current = null;
      }

      if (!arrastouMesmo || !pista) {
        setArrastando(false);
        setMovendo(false);
        return;
      }

      // Velocidade com que a mão soltou, em px/ms, pelo rastro recente.
      const pontos = rastro.current;
      const primeiro = pontos[0];
      const ultimo = pontos[pontos.length - 1];
      const dt = ultimo && primeiro ? ultimo.t - primeiro.t : 0;
      // O sinal é invertido: puxar para a esquerda faz o percurso avançar.
      const velocidade = dt > 0 ? -(ultimo.x - primeiro.x) / dt : 0;
      rastro.current = [];

      // `arrastando` continua LIGADO durante a inércia, e é o que mantém o
      // encaixe desligado — deixá-lo religar aqui faria o snap puxar a trilha de
      // volta no meio do deslize, contra o impulso. Ele desliga quando o
      // movimento assenta, e aí o encaixe faz o ajuste final.
      pararMovimento.current = lancar(pista, velocidade, () => {
        setArrastando(false);
        setMovendo(false);
        pararMovimento.current = null;
      });
    },
    [pistaRef]
  );

  // O `click` do nó dispara ao SOLTAR, então um arrasto que começou sobre um nó
  // abriria a fase ao terminar. Capturado na descida, antes de chegar ao botão.
  // `arrastou` só zera aqui, e não em `aoSoltar`: os dois eventos acontecem na
  // mesma interação e o clique vem depois.
  const aoClicarCapturando = useCallback(evento => {
    if (!arrastou.current) return;
    arrastou.current = false;
    evento.preventDefault();
    evento.stopPropagation();
  }, []);

  return {
    alcance,
    arrastando,
    movendo,
    deslizar,
    medirAlcance,
    // Espalhar direto no elemento rolável.
    ligacoes: {
      onPointerDown: aoApontar,
      onPointerMove: aoMover,
      onPointerUp: aoSoltar,
      onPointerCancel: aoSoltar,
      onClickCapture: aoClicarCapturando
    }
  };
}
